
// Extend the Window interface to include RUNTIME_CONFIG
declare global {
  interface Window {
    RUNTIME_CONFIG?: Record<string, string>;
  }
}

/**
 * Get runtime config value or fallback to environment variable
 */
const getRuntimeConfig = (key: string, fallback: string): string => {
  // Check if runtime config exists and has the key
  if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
    const value = window.RUNTIME_CONFIG[key];
    if (value && !value.startsWith('REPLACE_')) {
      return value;
    }
  }
  return fallback;
};

/**
 * Determine if we're in a container environment
 */
const isContainerEnvironment = (): boolean => {
  if (typeof window !== 'undefined') {
    // Check runtime config first
    const runtimeContainer = getRuntimeConfig('CONTAINER_ENVIRONMENT', 'false');
    if (runtimeContainer === 'true') {
      return true;
    }
    // Check window flag
    if ((window as any).CONTAINER_ENVIRONMENT === 'true') {
      return true;
    }
    // Check hostname (not localhost)
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return true;
    }
  }
  return import.meta.env.VITE_CONTAINER_ENVIRONMENT === 'true';
};

/**
 * Application configuration
 * Loads values from environment variables with fallbacks
 */
const config = {
  // API configuration
  api: {
    baseUrl: getRuntimeConfig('API_BASE_URL', 
      import.meta.env.VITE_API_BASE_URL || 
      getRuntimeConfig('API_MODULE1_URL', import.meta.env.VITE_API_MODULE1_URL || 'http://localhost:8000')
    ),
    timeout: 300000, // 5 minutes for file uploads
    retries: 1,
    module1: getRuntimeConfig('API_MODULE1_URL', import.meta.env.VITE_API_MODULE1_URL || 'http://localhost:8001'),
    module2: getRuntimeConfig('API_MODULE2_URL', import.meta.env.VITE_API_MODULE2_URL || 'http://localhost:8002'),
    module3: getRuntimeConfig('API_MODULE3_URL', import.meta.env.VITE_API_MODULE3_URL || 'http://localhost:8001'), // Module 3 uses same port as module 1
    module4: getRuntimeConfig('API_MODULE4_URL', import.meta.env.VITE_API_MODULE4_URL || 'http://localhost:8004'),
    snowflake: getRuntimeConfig('API_SNOWFLAKE_URL', import.meta.env.VITE_API_SNOWFLAKE_URL || 'http://localhost:8000'),
    // API Key configuration
    key: import.meta.env.VITE_API_KEY || '',
    keyEnabled: import.meta.env.VITE_API_KEY_ENABLED === 'true',
  },
  
  // Azure Blob Storage configuration
  azure: {
    storageAccountName: import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME || 'keywordclassifieralpha',
    storageAccountKey: import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_KEY,
    connectionString: import.meta.env.VITE_AZURE_STORAGE_CONNECTION_STRING,
    useApiProxy: import.meta.env.VITE_USE_API_PROXY === 'true',
    containers: {
      inputs: import.meta.env.VITE_BLOB_CONTAINER_INPUTS || 'inputs',
      outputs: import.meta.env.VITE_BLOB_CONTAINER_OUTPUTS || 'outputs',
      jobs: import.meta.env.VITE_BLOB_CONTAINER_JOBS || 'jobs',
      configs: import.meta.env.VITE_BLOB_CONTAINER_CONFIGS || 'configs',
      workflows: import.meta.env.VITE_BLOB_CONTAINER_WORKFLOWS || 'workflows',
    },
    blobBaseUrl: import.meta.env.VITE_BLOB_BASE_URL || 'http://localhost:10000/devstoreaccount1'
  },
  
  // Auth configuration
  auth: {
    enabled: import.meta.env.VITE_AUTH_ENABLED === 'true',
    username: import.meta.env.VITE_AUTH_USERNAME || 'admin',
    password: import.meta.env.VITE_AUTH_PASSWORD || 'admin',
    tokenExpiry: parseInt(import.meta.env.VITE_AUTH_TOKEN_EXPIRY || '86400', 10),
  },
  
  // Environment detection
  environment: {
    isContainer: isContainerEnvironment()
  }
};

export default config
